import { Children, useContext, type AnchorHTMLAttributes } from "react";
import type { Components } from "react-markdown";
import { parseCollectionVersionRef } from "../../../shared/references/contentReferences";
import MarkdownRenderer from "../../../ui/content/MarkdownRenderer";
import { TermLink } from "../../knowledgeData";
import { declaredNamespaces, useTermCardsByCommit } from "../../ontology";
import { ThreadContext } from "../page/threadPageContext";
import { runLinkKind } from "./runLinks";

/**
 * A link in a Run's text (Johannes, 4 Nov 15:59: "we should not assume things that aren't there
 * yet"): a term the data model knows at the Run's version opens DMW's own term card, by its name
 * or by the words the link was written with; an address DMW can't vouch for (`runLinkKind`: an
 * unknown one in the model's own namespaces), or any address before the model says what it is,
 * stays plain, copyable text with the link's words, never a link that may lead nowhere; any other
 * address is a link, bare or not. Outside a Run, every link stays a link.
 */
function RunLink({ href = "", children, node: _node, ...anchorProps }: AnchorHTMLAttributes<HTMLAnchorElement> & { node?: unknown }) {
  const thread = useContext(ThreadContext);
  const commit = thread === null ? null : (parseCollectionVersionRef(thread.overview.bindings.content_version)?.commit ?? null);
  const cards = useTermCardsByCommit(thread?.overview.projectId ?? null, commit === null ? [] : [commit]).get(commit ?? "") ?? null;
  // The address as written: the parser percent-encodes what isn't ASCII, so a term is looked up, and shown, decoded.
  const address = decoded(href);
  // An address written bare, autolinked: its words are the address itself, as the parser rewrites it (Sol round 2, S5).
  const words = Children.toArray(children);
  const text = words.length === 1 && typeof words[0] === "string" ? words[0] : null;
  const bare = text !== null && [text, `http://${text}`, `mailto:${text}`].some((written) => written === href || written === address);
  switch (commit === null ? "web" : runLinkKind(address, cards, cards === null ? new Set<string>() : declaredNamespaces(cards))) {
    case "term":
      return <TermLink iri={address} commit={commit} cards={cards}>{bare ? undefined : children}</TermLink>;
    case "unvouched":
      return bare ? <span className="break-all">{text}</span> : <span>{children} (<span className="break-all">{address}</span>)</span>;
    case "web":
      return <a {...anchorProps} href={href}>{children}</a>;
  }
}

/** `href` with its percent-encoding undone; as it is where it isn't valid encoding. */
function decoded(href: string): string {
  try {
    return decodeURI(href);
  } catch {
    return href;
  }
}

const RUN_COMPONENTS: Components = { a: RunLink };

/** A Run's Markdown: the app's renderer, with its links as `RunLink` says. */
export default function RunMarkdown({ content, className }: { content: string; className?: string }) {
  return <MarkdownRenderer content={content} className={className} components={RUN_COMPONENTS} />;
}
